import { useState } from 'react'
import Button from './Button'
import '../styles/CompanyUserAssignment.css'

/**
 * CompanyUserAssignment Component
 * A reusable component for assigning users to companies in a multi-step workflow
 *
 * @param {Object} props
 * @param {Array} props.companies - List of companies [{id, name, icon}]
 * @param {Array} props.users - List of users [{id, name, email, company, avatar}]
 * @param {Function} props.onAssign - Callback when assignment is completed (company, user) => void
 */
const CompanyUserAssignment = ({
  companies = [],
  users = [],
  onAssign
}) => {
  const [step, setStep] = useState(1) // 1: Select Company, 2: Select User, 3: Confirm Assignment
  const [selectedCompany, setSelectedCompany] = useState(null)
  const [selectedUser, setSelectedUser] = useState(null)
  const [searchCompany, setSearchCompany] = useState('')
  const [searchUser, setSearchUser] = useState('')
  const [isAssigned, setIsAssigned] = useState(false)

  // Filter companies based on search
  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(searchCompany.toLowerCase())
  )

  // Filter users based on search
  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchUser.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchUser.toLowerCase())
  )

  const handleCompanySelect = (company) => {
    setSelectedCompany(company)
  }

  const handleUserSelect = (user) => {
    setSelectedUser(user)
  }

  const handleNext = () => {
    if (step === 1 && selectedCompany) {
      setStep(2)
    } else if (step === 2 && selectedUser) {
      setStep(3)
    }
  }

  const handleBack = () => {
    if (step === 3) {
      setStep(2)
      setIsAssigned(false)
    } else if (step === 2) {
      setStep(1)
      setSelectedUser(null)
    }
  }

  const handleAssign = () => {
    setIsAssigned(true)
    if (onAssign) {
      onAssign(selectedCompany, selectedUser)
    }
  }

  const handleReset = () => {
    setStep(1)
    setSelectedCompany(null)
    setSelectedUser(null)
    setSearchCompany('')
    setSearchUser('')
    setIsAssigned(false)
  }

  return (
    <div className="company-user-assignment">
      <div className="assignment-steps">
        <div className={`step-indicator ${step >= 1 ? 'active' : ''}`}>
          <span className="step-number">1</span>
          <span className="step-label">Select Company</span>
        </div>
        <div className="step-connector"></div>
        <div className={`step-indicator ${step >= 2 ? 'active' : ''}`}>
          <span className="step-number">2</span>
          <span className="step-label">Select User</span>
        </div>
        <div className="step-connector"></div>
        <div className={`step-indicator ${step >= 3 ? 'active' : ''}`}>
          <span className="step-number">3</span>
          <span className="step-label">Assign to Company</span>
        </div>
      </div>

      <div className="assignment-content">
        {/* Step 1: Select Company */}
        {step === 1 && (
          <div className="assignment-panel">
            <div className="panel-header">
              <h2>Select a Company</h2>
            </div>
            <div className="panel-body">
              <div className="search-box">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  placeholder="Search companies..."
                  value={searchCompany}
                  onChange={(e) => setSearchCompany(e.target.value)}
                />
              </div>
              <div className="list-container">
                {filteredCompanies.map((company) => (
                  <div
                    key={company.id}
                    className={`list-item ${selectedCompany?.id === company.id ? 'selected' : ''}`}
                    onClick={() => handleCompanySelect(company)}
                  >
                    <span className="item-icon">{company.icon || '🏢'}</span>
                    <span className="item-name">{company.name}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="panel-footer">
              <Button
                variant="blue"
                onClick={handleNext}
                disabled={!selectedCompany}
                fullWidth
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Select User */}
        {step === 2 && (
          <div className="assignment-panel">
            <div className="panel-header">
              <h2>Select User</h2>
            </div>
            <div className="panel-body">
              <div className="search-box">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchUser}
                  onChange={(e) => setSearchUser(e.target.value)}
                />
              </div>
              <div className="list-container">
                {filteredUsers.map((user) => (
                  <div
                    key={user.id}
                    className={`list-item user-item ${selectedUser?.id === user.id ? 'selected' : ''}`}
                    onClick={() => handleUserSelect(user)}
                  >
                    <div className="user-avatar">
                      {user.avatar ? (
                        <img src={user.avatar} alt={user.name} />
                      ) : (
                        <span>{user.name.charAt(0)}</span>
                      )}
                    </div>
                    <div className="user-info">
                      <div className="user-name">{user.name}</div>
                      <div className="user-company">{user.company}</div>
                    </div>
                    <div className="checkbox-container">
                      <input
                        type="checkbox"
                        checked={selectedUser?.id === user.id}
                        readOnly
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="panel-footer">
              <Button
                variant="gray"
                onClick={handleBack}
                style={{ marginRight: '10px' }}
              >
                Back
              </Button>
              <Button
                variant="green"
                onClick={handleNext}
                disabled={!selectedUser}
              >
                Assign
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Confirm Assignment */}
        {step === 3 && (
          <div className="assignment-panel confirmation-panel">
            {!isAssigned ? (
              <>
                <div className="panel-header">
                  <h2>Assign to Company</h2>
                </div>
                <div className="panel-body">
                  <div className="confirmation-message">
                    <p>Assign <strong className="user-highlight">{selectedUser?.name}</strong> to</p>
                    <p><strong className="company-highlight">{selectedCompany?.name}</strong>?</p>
                  </div>
                </div>
                <div className="panel-footer">
                  <Button
                    variant="gray"
                    onClick={handleBack}
                    style={{ marginRight: '10px' }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="green"
                    onClick={handleAssign}
                  >
                    Assign
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="panel-body">
                  <div className="success-message">
                    <div className="success-icon">✓</div>
                    <p><strong>{selectedUser?.name}</strong> has been assigned</p>
                    <p>to <strong>{selectedCompany?.name}</strong>!</p>
                  </div>
                </div>
                <div className="panel-footer">
                  <Button
                    variant="blue"
                    onClick={handleReset}
                    fullWidth
                  >
                    Assign Another User
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default CompanyUserAssignment
